import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Dayjs } from 'dayjs';
import { CoachCalendarService } from '../../../core/services/coach-calendar.service';
import { isSameDay, tz } from '../../../core/helpers/calendar.helper';

/** How far ahead the coach may plan from this dialog. */
const DAYS_AHEAD = 14;

interface IFreeSlot {
  start: string;
  end: string;
}

interface IDayOption {
  date: Dayjs;
  weekday: string;
  dayNumber: string;
  slotCount: number;
}

/**
 * Plans a meeting with one student without leaving their page: pick a day, pick one of the coach's
 * own free slots, done. The times come from the API, so availability, meeting length and the slots
 * already taken are respected without repeating those rules here.
 */
@Component({
  selector: 'coach-plan-meeting-modal',
  templateUrl: './plan-meeting-modal.component.html',
  styleUrl: './plan-meeting-modal.component.scss',
})
export class PlanMeetingModalComponent implements OnInit, OnDestroy {
  @Input() studentId!: number;
  @Input() studentName?: string | null;
  @Input() levelKey?: string | null;

  @ViewChild('dayStrip') dayStrip?: ElementRef<HTMLElement>;

  days: IDayOption[] = [];
  selectedDay?: Dayjs;
  selectedStart?: string;

  /** Placeholder count, so the dialog keeps its height while the slots are on their way. */
  skeletonSlots = new Array(9);

  atStripStart = true;
  atStripEnd = false;

  isLoading = false;
  isSaving = false;
  errKey?: string;

  private freeSlots: IFreeSlot[] = [];

  destroyed$ = new Subject();

  constructor(
    public modal: NgbActiveModal,
    private calendarService: CoachCalendarService,
    private translate: TranslateService,
  ) {
  }

  ngOnInit(): void {
    this.getFreeSlots();
  }

  getFreeSlots() {
    const from = tz().startOf('day');
    const to = from.add(DAYS_AHEAD - 1, 'day').endOf('day');

    this.isLoading = true;
    this.calendarService.getFreeSlots(from.toDate(), to.toDate())
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        const now = tz();
        // Slots earlier today are still returned by the API; they are of no use here.
        this.freeSlots = ((r.data ?? []) as IFreeSlot[]).filter(slot => tz(slot.start).isAfter(now));

        this.buildDays(from);
        this.selectDay(this.days.find(day => day.slotCount)?.date);
        setTimeout(() => this.onStripScroll());
      });
  }

  /** The arrows move the strip by roughly one screenful of days. */
  scrollDays(direction: -1 | 1) {
    const strip = this.dayStrip?.nativeElement;
    if (!strip) {
      return;
    }

    strip.scrollBy({left: direction * Math.round(strip.clientWidth * 0.8), behavior: 'smooth'});
  }

  onStripScroll() {
    const strip = this.dayStrip?.nativeElement;
    if (!strip) {
      return;
    }

    this.atStripStart = strip.scrollLeft <= 1;
    this.atStripEnd = (strip.scrollLeft + strip.clientWidth) >= (strip.scrollWidth - 1);
  }

  buildDays(from: Dayjs) {
    this.days = Array.from({length: DAYS_AHEAD}, (_, index) => {
      const date = from.add(index, 'day');

      return {
        date,
        weekday: date.format('ddd').toLocaleUpperCase('tr-TR'),
        dayNumber: date.format('D'),
        slotCount: this.freeSlots.filter(slot => isSameDay(tz(slot.start), date)).length,
      };
    });
  }

  slotsOfSelectedDay(): IFreeSlot[] {
    if (!this.selectedDay) {
      return [];
    }

    return this.freeSlots.filter(slot => isSameDay(tz(slot.start), this.selectedDay));
  }

  selectDay(date?: Dayjs) {
    if (!date) {
      return;
    }

    this.selectedDay = date;
    this.selectedStart = undefined;
    this.errKey = undefined;
  }

  selectSlot(slot: IFreeSlot) {
    this.selectedStart = slot.start;
    this.errKey = undefined;
  }

  onSubmit() {
    if (!this.selectedStart) {
      return;
    }

    this.isSaving = true;
    this.calendarService.createAppointment({
      studentId: this.studentId,
      title: this.translate.instant('students.meeting_default_title'),
      startsAt: this.selectedStart,
    })
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isSaving = false))
      .subscribe(() => {
        this.modal.close(true);
      }, error => {
        // A slot can be taken between loading the list and submitting it; the weekly allowance and
        // availability are checked by the API as well.
        this.errKey = (error?.code === 409) ? 'agenda.errors.slot_taken' : 'agenda.errors.create_failed';
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
